import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TablePagination,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const API_URL = import.meta.env.VITE_API_URL;
const IMAGE_URL = import.meta.env.VITE_IMAGE_URL;

interface Product {
  productId: number;
  name: string;
  imageUrl: string;
}

interface SubCategory {
  subCategoryId: number;
  name: string;
  description: string;
  isActive: boolean;
  index: number;
}

const SubCategoryEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [subCategory, setSubCategory] = React.useState<SubCategory | null>(
    null,
  );
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const [products, setProducts] = React.useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = React.useState(false);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  React.useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/sub-categories/${id}`)
      .then((res) => setSubCategory(res.data))
      .catch(() => alert('Не вдалося завантажити підкатегорію'))
      .finally(() => setLoading(false));
  }, [id]);

  const fetchProducts = React.useCallback(() => {
    if (!subCategory) return;
    setProductsLoading(true);
    axios
      .get(
        `${API_URL}/products?subCategoryId=${subCategory.subCategoryId}&page=${page + 1}&limit=${rowsPerPage}`,
      )
      .then((res) => {
        setProducts(res.data.data);
        setTotal(res.data.total || 0);
      })
      .catch(() => alert('Не вдалося завантажити продукти'))
      .finally(() => setProductsLoading(false));
  }, [subCategory?.subCategoryId, page, rowsPerPage]);

  React.useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  if (loading) return <CircularProgress />;

  if (!subCategory) return <Typography>Підкатегорія не знайдена</Typography>;

  const handleChange = (field: keyof SubCategory, value: any) => {
    setSubCategory((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/sub-categories/${id}`, subCategory);
      navigate('/sub-categories');
    } catch {
      alert('Помилка при збереженні');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Видалити підкатегорію?')) return;
    try {
      await axios.delete(`${API_URL}/sub-categories/${id}`);
      navigate('/sub-categories');
    } catch {
      alert('Помилка при видаленні');
    }
  };

  return (
    <Box sx={{ mx: 5, mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Редагування підкатегорії: {subCategory.name}
      </Typography>

      <TextField
        fullWidth
        label="Назва"
        value={subCategory.name}
        onChange={(e) => handleChange('name', e.target.value)}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Опис"
        value={subCategory.description}
        onChange={(e) => handleChange('description', e.target.value)}
        sx={{ mb: 2 }}
      />

      <TextField
        type="number"
        fullWidth
        label="Порядок"
        value={subCategory.index ?? 1}
        inputProps={{ min: 1, max: 100 }}
        onChange={(e) =>
          handleChange(
            'index',
            Math.min(100, Math.max(1, Number(e.target.value))),
          )
        }
        sx={{ mb: 2 }}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={!!subCategory.isActive}
            onChange={(e) => handleChange('isActive', e.target.checked)}
          />
        }
        label="Показувати у боті"
        sx={{ mb: 2, display: 'block' }}
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Зберігаю...' : 'Зберегти'}
        </Button>
        <Button variant="outlined" color="error" onClick={handleDelete}>
          Видалити
        </Button>
        <Button variant="text" onClick={() => navigate('/sub-categories')}>
          Відмінити
        </Button>
      </Box>

      {productsLoading ? (
        <CircularProgress />
      ) : products.length > 0 ? (
        <Box>
          <Typography variant="h5" gutterBottom>
            Продукти підкатегорії
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Фото</TableCell>
                <TableCell>Назва</TableCell>
                <TableCell align="right">Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.productId}>
                  <TableCell>
                    <img
                      src={`${IMAGE_URL}${p.imageUrl}`}
                      alt={p.name}
                      width={50}
                      height={50}
                    />
                  </TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => navigate(`/products/${p.productId}`)}
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20, 50]}
          />
        </Box>
      ) : (
        <Typography>Продукти відсутні</Typography>
      )}
    </Box>
  );
};

export default SubCategoryEdit;
